import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Spin, Typography, Space } from 'antd';
import { GoogleOutlined, SafetyCertificateOutlined, LockOutlined } from '@ant-design/icons';
import authService from '@/services/authService';

const { Title, Text } = Typography;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  useEffect(() => {
    // Check if already authenticated
    if (authService.isAuthenticated()) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Check if Google SDK is loaded
    const checkGoogleLoaded = () => {
      const googleLoaded = typeof window !== 'undefined' && window.google;

      if (googleLoaded) {
        setIsGoogleLoaded(true);
        if (GOOGLE_CLIENT_ID) {
          authService.initializeGoogleAuth(GOOGLE_CLIENT_ID);
        }
      } else {
        setTimeout(checkGoogleLoaded, 100);
      }
    };
    checkGoogleLoaded();
  }, [navigate]);

  const handleGoogleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('🔘 Admin clicked "Continue with Google" button');
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.googleSignIn();

      console.log('📥 Authentication response received:', response);

      if (response.success) {
        console.log('🎉 Authentication successful! Redirecting to dashboard...');
        console.log('👤 Authenticated admin:', response.adminUser);
        navigate('/dashboard', { replace: true });
      } else {
        console.error('❌ Authentication failed:', response.message);
        setError(response.message || 'Authentication failed. Please try again.');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('💥 Google Sign-In error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <Card
        className="w-full max-w-md mx-5 shadow-md rounded-lg border border-gray-200"
        bodyStyle={{ padding: '48px 40px' }}
      >
        {/* Logo/Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-18 h-18 bg-primary-500 rounded-xl flex items-center justify-center">
            <SafetyCertificateOutlined className="text-4xl text-white" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <Title level={2} className="mb-2 text-3xl font-semibold text-gray-800">
            Beemi Admin
          </Title>
          <Text className="text-base text-gray-500">
            Sign in to your admin account
          </Text>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            description={error}
            type="error"
            closable
            showIcon
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}

        {/* Sign In Button */}
        <Button
          type="primary"
          size="large"
          block
          icon={<GoogleOutlined />}
          onClick={handleGoogleSignIn}
          loading={isLoading}
          disabled={!isGoogleLoaded}
          className="h-12 text-base font-medium"
        >
          {isLoading ? 'Signing in...' : 'Continue with Google'}
        </Button>

        {/* Loading State */}
        {!isGoogleLoaded && (
          <div className="text-center mt-5">
            <Space>
              <Spin size="small" />
              <Text type="secondary" className="text-sm">Loading...</Text>
            </Space>
          </div>
        )}

        {/* Security Notice */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <Space direction="vertical" size={8} className="w-full">
            <LockOutlined className="text-base text-secondary-500" />
            <Text className="text-sm text-gray-500 block">
              Only authorized administrators can access this panel
            </Text>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
